import { applyMove, initialPosition } from '@billycheckers/shared';
import type { Color, GameResult, Move, Position, RoomState } from '@billycheckers/shared';
import { BoardView } from '../board.js';

export interface GameHandlers {
  onLeave(): void;
  onRematch(accept: boolean): void;
  onMove(move: Move): void;
  onResign(): void;
  onDraw(action: 'offer' | 'accept' | 'decline'): void;
  onChat(text: string): void;
}

const REASON_RU: Record<string, string> = {
  'no-pieces': 'шашек не осталось',
  'no-moves': 'ходов больше нет',
  repetition: 'троекратное повторение позиции',
  'kings-15': '15 ходов только дамками',
  agreement: 'по соглашению',
  resign: 'сдача',
  timeout: 'время вышло',
};

interface SeatEls {
  root: HTMLElement;
  who: HTMLElement;
  taken: HTMLElement;
  clock: HTMLElement;
}

/** Экран партии: плашки, доска, статус, вкладки «ходы»/«чат», оверлей конца. */
export class GameScreen {
  readonly el: HTMLElement;
  readonly board: BoardView;
  private seatTop: SeatEls;
  private seatBot: SeatEls;
  private statusEl: HTMLElement;
  private drawRow: HTMLElement;
  private overlayEl: HTMLElement | null = null;
  private overlayKey = '';
  private state: RoomState | null = null;
  private hintTimer: number | null = null;
  private resignBtn: HTMLButtonElement;
  private drawBtn: HTMLButtonElement;
  private resignArmed = false;

  // вкладки
  private tabMoves: HTMLButtonElement;
  private tabChat: HTMLButtonElement;
  private panel: HTMLElement;
  private movesEl: HTMLElement;
  private chatWrap: HTMLElement;
  private chatLog: HTMLElement;
  private chatInput: HTMLInputElement;
  private activeTab: 'moves' | 'chat' | null = null;
  private chatRoomId: string | null = null;
  private unreadChat = 0;

  // просмотр истории
  private viewIndex: number | null = null;
  private toCurrentBtn: HTMLButtonElement;

  constructor(private h: GameHandlers) {
    this.el = document.createElement('div');
    this.el.style.display = 'contents';

    this.seatTop = makeSeat();
    this.seatBot = makeSeat();

    const frame = document.createElement('div');
    frame.className = 'frame';
    this.board = new BoardView();
    frame.appendChild(this.board.el);

    this.board.onMove = (m) => h.onMove(m);
    this.board.onHint = (text) => this.flashStatus(text);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'status';

    // строка предложения ничьей (появляется при входящем предложении)
    this.drawRow = document.createElement('div');
    this.drawRow.className = 'bar hidden';

    const bar = document.createElement('div');
    bar.className = 'bar';

    const leave = document.createElement('button');
    leave.className = 'btn';
    leave.textContent = 'в лобби';
    leave.addEventListener('click', () => h.onLeave());

    this.drawBtn = document.createElement('button');
    this.drawBtn.className = 'btn';
    this.drawBtn.textContent = 'ничья';
    this.drawBtn.addEventListener('click', () => h.onDraw('offer'));

    this.resignBtn = document.createElement('button');
    this.resignBtn.className = 'btn';
    this.resignBtn.textContent = 'сдаться';
    this.resignBtn.addEventListener('click', () => {
      if (this.resignArmed) {
        this.resignArmed = false;
        this.resignBtn.textContent = 'сдаться';
        this.h.onResign();
      } else {
        this.resignArmed = true;
        this.resignBtn.textContent = 'точно сдаться?';
        window.setTimeout(() => {
          this.resignArmed = false;
          this.resignBtn.textContent = 'сдаться';
        }, 3000);
      }
    });

    // вкладки под доской (свёрнуты по умолчанию)
    this.tabMoves = document.createElement('button');
    this.tabMoves.className = 'btn';
    this.tabMoves.textContent = 'ходы';
    this.tabMoves.addEventListener('click', () => this.toggleTab('moves'));
    this.tabChat = document.createElement('button');
    this.tabChat.className = 'btn';
    this.tabChat.textContent = 'чат';
    this.tabChat.addEventListener('click', () => this.toggleTab('chat'));

    bar.append(leave, this.drawBtn, this.resignBtn, this.tabMoves, this.tabChat);

    this.panel = document.createElement('div');
    this.panel.className = 'panel hidden';

    this.movesEl = document.createElement('ol');
    this.movesEl.className = 'moves';

    this.toCurrentBtn = document.createElement('button');
    this.toCurrentBtn.className = 'btn primary hidden';
    this.toCurrentBtn.textContent = 'к текущей';
    this.toCurrentBtn.addEventListener('click', () => this.setViewIndex(null));

    this.chatWrap = document.createElement('div');
    this.chatWrap.className = 'chat';
    this.chatLog = document.createElement('div');
    this.chatLog.className = 'chat-log';
    const chatForm = document.createElement('form');
    chatForm.className = 'chat-form';
    this.chatInput = document.createElement('input');
    this.chatInput.type = 'text';
    this.chatInput.maxLength = 500;
    this.chatInput.placeholder = 'сообщение…';
    const send = document.createElement('button');
    send.type = 'submit';
    send.className = 'btn';
    send.textContent = 'отпр.';
    chatForm.append(this.chatInput, send);
    chatForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = this.chatInput.value.trim();
      if (text) {
        this.h.onChat(text);
        this.chatInput.value = '';
      }
    });
    this.chatWrap.append(this.chatLog, chatForm);

    this.el.append(
      this.seatTop.root, frame, this.seatBot.root,
      this.statusEl, this.drawRow, bar, this.panel,
    );
  }

  // ── вкладки ───────────────────────────────────────────────────────────────

  private toggleTab(tab: 'moves' | 'chat'): void {
    this.activeTab = this.activeTab === tab ? null : tab;
    this.renderPanel();
  }

  private renderPanel(): void {
    this.panel.classList.toggle('hidden', this.activeTab === null);
    this.tabMoves.classList.toggle('active-tab', this.activeTab === 'moves');
    this.tabChat.classList.toggle('active-tab', this.activeTab === 'chat');
    if (this.activeTab === 'moves') {
      this.panel.replaceChildren(this.toCurrentBtn, this.movesEl);
      this.renderMoves();
    } else if (this.activeTab === 'chat') {
      this.panel.replaceChildren(this.chatWrap);
      this.unreadChat = 0;
      this.tabChat.textContent = 'чат';
      this.chatLog.scrollTop = this.chatLog.scrollHeight;
    }
  }

  // ── история ходов ─────────────────────────────────────────────────────────

  private renderMoves(): void {
    const st = this.state;
    if (!st) return;
    this.movesEl.replaceChildren();
    for (let i = 0; i < st.history.length; i += 2) {
      const li = document.createElement('li');
      const num = document.createElement('span');
      num.className = 'mnum';
      num.textContent = `${i / 2 + 1}.`;
      li.appendChild(num);
      for (const j of [i, i + 1]) {
        const e = st.history[j];
        if (!e) continue;
        const b = document.createElement('button');
        b.className = 'mv' + (this.viewIndex === j ? ' viewed' : '');
        b.textContent = e.notation;
        b.addEventListener('click', () => this.setViewIndex(j));
        li.appendChild(b);
      }
      this.movesEl.appendChild(li);
    }
    this.movesEl.scrollTop = this.movesEl.scrollHeight;
  }

  /** Просмотр позиции после хода index (null — вернуться к текущей). */
  private setViewIndex(index: number | null): void {
    const st = this.state;
    if (!st) return;
    this.viewIndex = index;
    this.toCurrentBtn.classList.toggle('hidden', index === null);
    if (index === null) {
      this.renderBoard();
      this.renderStatus();
    } else {
      let pos: Position = initialPosition();
      for (let j = 0; j <= index; j++) pos = applyMove(pos, st.history[j]!.move);
      this.board.setState(pos, null, st.history[index]!.move);
      this.statusEl.textContent = `просмотр: ход ${Math.floor(index / 2) + 1} · ${st.history[index]!.notation}`;
      this.statusEl.classList.add('warn');
    }
    if (this.activeTab === 'moves') this.renderMoves();
  }

  // ── чат ───────────────────────────────────────────────────────────────────

  addChat(from: string, text: string, ts: number): void {
    const row = document.createElement('div');
    row.className = 'chat-msg';
    const meta = document.createElement('span');
    meta.className = 'chat-meta';
    const d = new Date(ts);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    meta.textContent = `${hh}:${mm} ${from}`;
    const body = document.createElement('span');
    body.className = 'chat-text';
    // ТОЛЬКО textContent — защита от XSS (SPEC §6.4)
    body.textContent = text;
    row.append(meta, body);
    this.chatLog.appendChild(row);
    if (this.activeTab === 'chat') {
      this.chatLog.scrollTop = this.chatLog.scrollHeight;
    } else {
      this.unreadChat += 1;
      this.tabChat.textContent = `чат (${this.unreadChat})`;
    }
  }

  // ── синхронизация ─────────────────────────────────────────────────────────

  /** Полная синхронизация по room:state. */
  update(state: RoomState): void {
    const prev = this.state;
    const roomChanged = state.roomId !== this.chatRoomId;
    // не сбрасываем доску (и ввод цепочки!), если позиция не менялась:
    // room:state мог прийти из-за зрителя, реконнекта соперника и т.п.
    const boardDirty =
      !prev
      || roomChanged
      || prev.seq !== state.seq
      || prev.you !== state.you
      || prev.roomStatus !== state.roomStatus;
    this.state = state;
    if (roomChanged) {
      this.chatRoomId = state.roomId;
      this.chatLog.replaceChildren();
      this.unreadChat = 0;
      this.tabChat.textContent = 'чат';
      this.viewIndex = null;
      this.toCurrentBtn.classList.add('hidden');
    }
    if (this.viewIndex !== null && this.viewIndex >= state.history.length) {
      this.viewIndex = null;
      this.toCurrentBtn.classList.add('hidden');
    }
    if (this.viewIndex === null && boardDirty) this.renderBoard();
    this.renderSeats();
    // в режиме просмотра истории подпись «просмотр: ход N» не затираем
    if (this.viewIndex === null) this.renderStatus();
    this.renderControls();
    if (this.activeTab === 'moves') this.renderMoves();
    if (state.result) {
      this.showGameOver(state.result);
    } else {
      this.hideOverlay();
    }
  }

  private renderBoard(): void {
    const st = this.state;
    if (!st) return;
    const you: Color | null = st.you === 'spectator' ? null : st.you;
    this.board.setState(st.position, you, st.lastMove);
  }

  /**
   * Применение move:applied с проверкой номера хода (SPEC §4):
   * старый seq отбрасывается, пропуск в нумерации требует пересинхронизации.
   */
  applyMoveMsg(
    seq: number, move: Move, position: Position,
    clocks: { white: number; black: number } | null,
  ): 'applied' | 'stale' | 'gap' {
    if (!this.state) return 'stale';
    if (seq <= this.state.seq) return 'stale';
    if (seq > this.state.seq + 1) return 'gap';
    this.state = { ...this.state, position, clocks, lastMove: move, seq };
    if (this.viewIndex === null) {
      this.board.applyServerMove(move, position);
    }
    this.renderSeats();
    if (this.viewIndex === null) this.renderStatus();
    return 'applied';
  }

  /** Принудительная перерисовка доски по текущему состоянию (resync после error). */
  refreshBoard(): void {
    if (this.viewIndex === null) this.renderBoard();
  }

  appendHistory(entry: RoomState['history'][number]): void {
    if (!this.state) return;
    this.state.history.push(entry);
    if (this.activeTab === 'moves') this.renderMoves();
  }

  setClocks(white: number, black: number): void {
    if (!this.state || !this.state.clocks) return;
    this.state = { ...this.state, clocks: { white, black } };
    const { top, bot } = this.orientationSides();
    this.seatTop.clock.textContent = fmtClock(top === 'white' ? white : black);
    this.seatBot.clock.textContent = fmtClock(bot === 'white' ? white : black);
  }

  showGameOver(result: GameResult): void {
    if (!this.state) return;
    // не пересоздавать оверлей, если ничего не поменялось — иначе клик по «Реванш»
    // теряется, когда между mousedown и mouseup приходит room:state (вошёл зритель)
    const key = `${result.result}|${result.reason}|${this.state.rematchOfferFrom ?? '-'}|${this.state.you}`;
    if (this.overlayEl && this.overlayKey === key) return;
    this.overlayKey = key;
    this.hideOverlay();
    const overlay = document.createElement('div');
    overlay.className = 'overlay';
    const card = document.createElement('div');
    card.className = 'card';
    const big = document.createElement('div');
    big.className = 'big';
    const you = this.state.you;
    if (result.result === 'draw') {
      big.textContent = 'Ничья';
    } else if (you === 'spectator') {
      big.textContent = result.result === 'white' ? 'Белые победили' : 'Чёрные победили';
    } else {
      big.textContent = result.result === you ? 'Победа!' : 'Поражение';
    }
    const why = document.createElement('div');
    why.className = 'why';
    why.textContent = REASON_RU[result.reason] ?? result.reason;
    const row = document.createElement('div');
    row.className = 'row';
    if (you !== 'spectator') {
      const rematch = document.createElement('button');
      rematch.className = 'btn primary';
      const offer = this.state.rematchOfferFrom;
      if (offer && offer !== you) {
        rematch.textContent = 'Принять реванш';
        rematch.addEventListener('click', () => this.h.onRematch(true));
      } else if (offer === you) {
        rematch.textContent = 'Реванш предложен…';
        rematch.disabled = true;
      } else {
        rematch.textContent = 'Реванш';
        rematch.addEventListener('click', () => this.h.onRematch(false));
      }
      row.appendChild(rematch);
    }
    const toLobby = document.createElement('button');
    toLobby.className = 'btn';
    toLobby.textContent = 'В лобби';
    toLobby.addEventListener('click', () => this.h.onLeave());
    row.appendChild(toLobby);
    card.append(big, why, row);
    overlay.appendChild(card);
    this.el.appendChild(overlay);
    this.overlayEl = overlay;
  }

  hideOverlay(): void {
    this.overlayEl?.remove();
    this.overlayEl = null;
  }

  // ── внутренности ──────────────────────────────────────────────────────────

  /** Чей цвет сверху/снизу: свой всегда снизу, у зрителя белые снизу. */
  private orientationSides(): { top: Color; bot: Color } {
    const you = this.state?.you;
    return you === 'black' ? { top: 'white', bot: 'black' } : { top: 'black', bot: 'white' };
  }

  private renderSeats(): void {
    const st = this.state;
    if (!st) return;
    const { top, bot } = this.orientationSides();
    this.renderSeat(this.seatTop, top);
    this.renderSeat(this.seatBot, bot);
    const turn = st.position.turn;
    this.seatTop.root.classList.toggle('on', !st.result && turn === top);
    this.seatBot.root.classList.toggle('on', !st.result && turn === bot);
  }

  private renderSeat(seat: SeatEls, color: Color): void {
    const st = this.state!;
    const info = color === 'white' ? st.white : st.black;
    seat.who.replaceChildren();
    seat.who.append(info?.name ?? '…');
    if (info && !info.connected) {
      const off = document.createElement('span');
      off.className = 'off';
      off.textContent = ' · отключился';
      seat.who.appendChild(off);
    }
    // стопка съеденных: у игрока лежат фишки цвета соперника
    const enemy: Color = color === 'white' ? 'black' : 'white';
    const alive = countColor(st.position, enemy);
    const eaten = 12 - alive;
    if (seat.taken.childElementCount !== eaten) {
      seat.taken.replaceChildren();
      for (let i = 0; i < eaten; i++) {
        const chip = document.createElement('i');
        chip.className = enemy === 'white' ? 'w' : 'b';
        seat.taken.appendChild(chip);
      }
    }
    seat.clock.textContent = st.clocks
      ? fmtClock(color === 'white' ? st.clocks.white : st.clocks.black)
      : '';
  }

  private renderControls(): void {
    const st = this.state;
    if (!st) return;
    const isPlayer = st.you !== 'spectator';
    const playing = st.roomStatus === 'playing';
    this.resignBtn.classList.toggle('hidden', !isPlayer || !playing);
    this.drawBtn.classList.toggle('hidden', !isPlayer || !playing);
    this.drawBtn.disabled = st.drawOfferFrom !== null;
    // входящее предложение ничьей
    this.drawRow.replaceChildren();
    const incoming = isPlayer && playing && st.drawOfferFrom !== null && st.drawOfferFrom !== st.you;
    this.drawRow.classList.toggle('hidden', !incoming);
    if (incoming) {
      const label = document.createElement('span');
      label.className = 'status warn';
      label.textContent = 'соперник предлагает ничью';
      const yes = document.createElement('button');
      yes.className = 'btn primary';
      yes.textContent = 'принять';
      yes.addEventListener('click', () => this.h.onDraw('accept'));
      const no = document.createElement('button');
      no.className = 'btn';
      no.textContent = 'отклонить';
      no.addEventListener('click', () => this.h.onDraw('decline'));
      this.drawRow.append(label, yes, no);
    }
  }

  private renderStatus(): void {
    const st = this.state;
    if (!st) return;
    this.statusEl.classList.remove('warn');
    const specs = st.spectators > 0 ? ` · зрителей: ${st.spectators}` : '';
    if (st.result) {
      this.statusEl.textContent = 'партия завершена' + specs;
      return;
    }
    if (st.roomStatus === 'waiting') {
      this.statusEl.textContent = 'ждём соперника — пришлите другу ссылку на эту страницу';
      return;
    }
    if (st.you === 'spectator') {
      this.statusEl.textContent =
        `вы смотрите · ход ${st.position.turn === 'white' ? 'белых' : 'чёрных'}${specs}`;
      return;
    }
    if (st.drawOfferFrom === st.you) {
      this.statusEl.textContent = 'ничья предложена…' + specs;
      return;
    }
    this.statusEl.textContent = (st.position.turn === st.you ? 'ваш ход' : 'ход соперника') + specs;
  }

  private flashStatus(text: string): void {
    this.statusEl.textContent = text;
    this.statusEl.classList.add('warn');
    if (this.hintTimer !== null) window.clearTimeout(this.hintTimer);
    this.hintTimer = window.setTimeout(() => this.renderStatus(), 1600);
  }
}

function makeSeat(): SeatEls {
  const root = document.createElement('div');
  root.className = 'seat';
  const who = document.createElement('span');
  who.className = 'who';
  const taken = document.createElement('span');
  taken.className = 'taken';
  const clock = document.createElement('span');
  clock.className = 'clock';
  root.append(who, taken, clock);
  return { root, who, taken, clock };
}

function countColor(pos: Position, color: Color): number {
  let n = 0;
  for (const c of pos.board) {
    if (color === 'white' ? c === 1 || c === 2 : c === 3 || c === 4) n++;
  }
  return n;
}

function fmtClock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
